/**
 * Calendar Service - Handles Google Calendar and Apple Calendar integration
 */

/**
 * Generate .ics file content for Apple Calendar / iCal
 */
export const generateICSFile = (viewing) => {
  const startDate = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
  const endDate = viewing.endAt?.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);
  
  // Format dates for ICS (YYYYMMDDTHHMMSS)
  const formatICSDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}T${hours}${minutes}${seconds}`;
  };

  const uid = viewing.id || `viewing-${Date.now()}`;
  const summary = `Bezichtiging - ${viewing.propertyTitle || viewing.listingId || 'Property'}`;
  const description = `Bezichtiging gepland${viewing.tenantName ? ` met ${viewing.tenantName}` : ''}${viewing.location ? `\nLocatie: ${viewing.location}` : ''}${viewing.notes ? `\nNotities: ${viewing.notes}` : ''}`;
  const location = viewing.location || viewing.propertyAddress || '';

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//StudyStay//StudyStay Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}@studystay.org`,
    `DTSTAMP:${formatICSDate(new Date())}`,
    `DTSTART:${formatICSDate(startDate)}`,
    `DTEND:${formatICSDate(endDate)}`,
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
    `LOCATION:${location}`,
    'STATUS:CONFIRMED',
    'SEQUENCE:0',
    'END:VEVENT',
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
};

/**
 * Download .ics file for Apple Calendar
 */
export const downloadICSFile = (viewing, filename = null) => {
  const icsContent = generateICSFile(viewing);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `bezichtiging-${viewing.id || Date.now()}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate Google Calendar URL
 */
export const generateGoogleCalendarURL = (viewing) => {
  const startDate = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
  const endDate = viewing.endAt?.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);
  
  // Format dates for Google Calendar (YYYYMMDDTHHMMSS)
  const formatGoogleDate = (date) => {
    return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  };

  const title = encodeURIComponent(`Bezichtiging - ${viewing.propertyTitle || viewing.listingId || 'Property'}`);
  const details = encodeURIComponent(
    `Bezichtiging gepland${viewing.tenantName ? ` met ${viewing.tenantName}` : ''}${viewing.location ? `\nLocatie: ${viewing.location}` : ''}${viewing.notes ? `\nNotities: ${viewing.notes}` : ''}`
  );
  const location = encodeURIComponent(viewing.location || viewing.propertyAddress || '');
  const start = formatGoogleDate(startDate);
  const end = formatGoogleDate(endDate);

  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${start}/${end}&details=${details}&location=${location}`;
};

/**
 * Open Google Calendar in new window
 */
export const openGoogleCalendar = (viewing) => {
  const url = generateGoogleCalendarURL(viewing);
  window.open(url, '_blank');
};

/**
 * Generate .ics file for multiple viewings (bulk export)
 */
export const generateBulkICSFile = (viewings) => {
  const events = viewings.map(viewing => {
    const startDate = viewing.startAt?.toDate ? viewing.startAt.toDate() : new Date(viewing.startAt);
    const endDate = viewing.endAt?.toDate ? viewing.endAt.toDate() : new Date(viewing.endAt);
    
    const formatICSDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}${month}${day}T${hours}${minutes}${seconds}`;
    };

    const uid = viewing.id || `viewing-${Date.now()}-${Math.random()}`;
    const summary = `Bezichtiging - ${viewing.propertyTitle || viewing.listingId || 'Property'}`;
    const description = `Bezichtiging gepland${viewing.tenantName ? ` met ${viewing.tenantName}` : ''}${viewing.location ? `\nLocatie: ${viewing.location}` : ''}${viewing.notes ? `\nNotities: ${viewing.notes}` : ''}`;
    const location = viewing.location || viewing.propertyAddress || '';

    return [
      'BEGIN:VEVENT',
      `UID:${uid}@studystay.org`,
      `DTSTAMP:${formatICSDate(new Date())}`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      `SUMMARY:${summary}`,
      `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
      `LOCATION:${location}`,
      'STATUS:CONFIRMED',
      'SEQUENCE:0',
      'END:VEVENT'
    ].join('\r\n');
  }).join('\r\n');

  const icsContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//StudyStay//StudyStay Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    events,
    'END:VCALENDAR'
  ].join('\r\n');

  return icsContent;
};

/**
 * Download bulk .ics file
 */
export const downloadBulkICSFile = (viewings, filename = 'studystay-bezichtigingen.ics') => {
  const icsContent = generateBulkICSFile(viewings);
  const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Generate Webcal subscription URL for Apple Calendar
 * This creates a subscription URL that Apple Calendar can subscribe to for automatic updates
 * Note: This requires a backend endpoint that serves .ics files dynamically
 */
export const generateWebcalSubscriptionURL = (userId) => {
  // In production, this would be your backend URL
  const baseUrl = window.location.origin;
  // For now, we'll use a placeholder that can be replaced with actual backend
  return `${baseUrl}/api/calendar/${userId}/viewings.ics`;
};

/**
 * Generate Google Calendar subscription URL
 * This creates a URL that can be used to subscribe to Google Calendar
 */
export const generateGoogleCalendarSubscriptionURL = (userId) => {
  const baseUrl = window.location.origin;
  // Google Calendar can subscribe to iCal feeds
  return `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(`${baseUrl}/api/calendar/${userId}/viewings.ics`)}`;
};

